<!--
  StaleCallout.vue (Phase 2.1 -- card https://trello.com/c/hYlwyKYT).

  Top-of-detail banner that surfaces when an ApprovalRecord's
  stale_state has advanced past 'active'. The banner copy varies by
  state so the builder reads, at a glance, what already happened and
  what the next sensible action is.

  State -> copy:
    reminded   -> "Reminder sent." + Send another reminder / Escalate
                  / Bypass.
    escalated  -> "Escalated to the account owner." + Send reminder /
                  Bypass.
    expired    -> "Approval link expired." + Bypass.

  Props:
    - staleState: string -- one of 'reminded' | 'escalated' | 'expired'.
    - pending: boolean

  Emits:
    - remind
    - escalate
    - bypass-open

  The actions emit the same event names as StatusAwareActionBar so the
  parent forwards them through the same handlers; the banner is a
  shortcut surface, not a separate action contract.
-->
<template>
    <div
        v-if="visible"
        class="aw-callout"
        :class="`aw-callout--${staleState}`"
        :data-testid="`stale-callout-${staleState}`"
        role="alert"
    >
        <div class="aw-callout__body">
            <strong class="aw-callout__title">{{ title }}</strong>
            <span class="aw-callout__detail">{{ detail }}</span>
        </div>
        <div class="aw-callout__actions">
            <button
                v-if="showRemind"
                type="button"
                class="aw-callout__btn"
                :disabled="pending"
                data-testid="stale-callout-remind"
                @click="$emit('remind')"
            >
                Send reminder
            </button>
            <button
                v-if="showEscalate"
                type="button"
                class="aw-callout__btn"
                :disabled="pending"
                data-testid="stale-callout-escalate"
                @click="$emit('escalate')"
            >
                Escalate
            </button>
            <button
                type="button"
                class="aw-callout__btn aw-callout__btn--primary"
                :disabled="pending"
                data-testid="stale-callout-bypass"
                @click="$emit('bypass-open')"
            >
                Bypass approval
            </button>
        </div>
    </div>
</template>

<script>
export default {
    name: 'StaleCallout',
    props: {
        staleState: {
            type: String,
            default: 'active',
        },
        pending: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['remind', 'escalate', 'bypass-open'],
    computed: {
        visible() {
            return ['reminded', 'escalated', 'expired'].includes(
                this.staleState,
            )
        },
        title() {
            if (this.staleState === 'reminded') return 'Reminder sent.'
            if (this.staleState === 'escalated') {
                return 'Escalated to the account owner.'
            }
            if (this.staleState === 'expired') {
                return 'Approval link expired.'
            }
            return ''
        },
        detail() {
            if (this.staleState === 'reminded') {
                return 'The client was reminded automatically. Resend manually, escalate, or bypass to keep production moving.'
            }
            if (this.staleState === 'escalated') {
                return 'The account owner has been notified. Continue reminding the client, or bypass if you have verbal sign-off.'
            }
            if (this.staleState === 'expired') {
                return 'The 14-day approval window passed without a client response. Bypass with a documented reason to clear production.'
            }
            return ''
        },
        showRemind() {
            return ['reminded', 'escalated'].includes(this.staleState)
        },
        showEscalate() {
            return this.staleState === 'reminded'
        },
    },
}
</script>

<style scoped>
.aw-callout {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 12px 14px;
    border-radius: var(--tf-radius-sm, 6px);
    margin-bottom: 16px;
    background: #fff8eb;
    border: 1px solid #c97900;
}
.aw-callout--escalated {
    background: #ffe9d6;
    border-color: #b34700;
}
.aw-callout--expired {
    background: #fde2e1;
    border-color: #c62828;
}
.aw-callout__body {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
}
.aw-callout__title {
    font-size: 13px;
    color: var(--tf-fg-1, #1a202c);
}
.aw-callout__detail {
    font-size: 12px;
    color: var(--tf-fg-3, #4a5568);
}
.aw-callout__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
}
.aw-callout__btn {
    padding: 4px 10px;
    border: 1px solid var(--tf-border-strong, #cbd5e0);
    background: var(--tf-bg, #ffffff);
    border-radius: 4px;
    font-size: 12px;
    cursor: pointer;
}
.aw-callout__btn--primary {
    background: var(--tf-primary, #1976d2);
    color: #ffffff;
    border-color: var(--tf-primary, #1976d2);
}
.aw-callout__btn:disabled {
    opacity: 0.55;
    cursor: not-allowed;
}
</style>
